import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Search, X } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminAccessDenied } from "@/components/admin/AdminAccessDenied";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["pending", "under_review", "verified", "rejected", "suspended"];

const inputClass = "w-full border border-ink/15 bg-ivory px-4 py-3 text-sm outline-none transition-colors focus:border-forest";

export default function AdminEmployers() {
    const { user, logout, hasPermission } = useAdminAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "", q: "" });
    const [selected, setSelected] = useState(null);
    const [notes, setNotes] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
        const res = await fetch(`${API}/api/admin/employers?${params}`, { credentials: "include" });
        if (res.ok) setItems(await res.json());
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        if (!user) return;
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [user, load]);

    const open = async (id) => {
        const res = await fetch(`${API}/api/admin/employers/${id}`, { credentials: "include" });
        if (res.ok) {
            setSelected(await res.json());
            setNotes("");
        }
    };

    const setVerification = async (status) => {
        const res = await fetch(`${API}/api/admin/employers/${selected.id}/verification`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status, notes }),
        });
        if (res.ok) {
            setSelected(null);
            setNotes("");
            load();
        }
    };

    const download = async (doc) => {
        const res = await fetch(`${API}/api/admin/employers/${selected.id}/documents/${doc.stored}`, { credentials: "include" });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.original;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!user) return <AdminLoading />;
    if (!hasPermission("employers")) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] text-ink">
                <AdminNav active="employers" email={user.email} user={user} onLogout={logout} />
                <AdminAccessDenied section="employer management" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory text-ink">
            <AdminNav active="employers" email={user.email} user={user} onLogout={logout} />
            <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <h1 className="font-serif text-3xl font-medium tracking-tight">Employers</h1>
                <p className="mt-1 text-sm text-ink/55">Review employer registrations, company profiles and verification requests.</p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <div className="relative flex-1">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/35" />
                        <input data-testid="admin-employer-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search company, contact or email…" className={`${inputClass} pl-11`} />
                    </div>
                    <select data-testid="admin-employer-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={`${inputClass} cursor-pointer sm:w-64`}>
                        <option value="">All statuses</option>
                        {STATUSES.map((status) => <option key={status} value={status}>{status.replace(/_/g, " ")}</option>)}
                    </select>
                </div>

                <div className="mt-8" data-testid="admin-employers-list">
                    {loading ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-forest" /></div> : items.length === 0 ? (
                        <p className="border-t border-ink/10 py-16 text-center text-sm text-ink/50">No employers match these filters.</p>
                    ) : items.map((item) => (
                        <button key={item.id} type="button" data-testid={`admin-employer-${item.id}`} onClick={() => open(item.id)} className="grid w-full grid-cols-[1fr_auto] items-center gap-5 border-t border-ink/10 px-2 py-5 text-left transition-colors hover:bg-sand sm:grid-cols-[1fr_auto_auto]">
                            <span className="min-w-0">
                                <span className="block truncate font-serif text-xl font-medium">{item.company_name || item.name}</span>
                                <span className="block truncate text-xs text-ink/50">{item.email} · {item.country || "Country not set"} · {item.jobs} jobs</span>
                            </span>
                            <span className="hidden text-xs text-ink/45 sm:block">{item.completion}% complete</span>
                            <span className="border border-ink/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/55">{item.verification_status.replace(/_/g, " ")}</span>
                        </button>
                    ))}
                    <div className="border-t border-ink/10" />
                </div>
            </main>

            {selected && (
                <div className="fixed inset-0 z-50" data-testid="admin-employer-detail">
                    <div className="absolute inset-0 bg-night/50" onClick={() => setSelected(null)} />
                    <aside className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto bg-ivory p-7 sm:p-10">
                        <div className="flex items-start justify-between gap-6">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/45">Employer review</p>
                                <h2 className="mt-2 font-serif text-3xl font-medium">{selected.profile.company_name || selected.name}</h2>
                                <p className="mt-1 text-sm text-ink/55">{selected.email}</p>
                            </div>
                            <button type="button" data-testid="admin-employer-close" onClick={() => setSelected(null)} className="border border-ink/15 p-2"><X size={18} /></button>
                        </div>
                        <div className="mt-8 border-t border-ink/10">
                            {["website", "country", "city", "business_address", "industry", "company_size", "contact_person", "business_email", "phone", "whatsapp", "registration_info", "recruitment_contact_name", "recruitment_contact_email", "recruitment_contact_phone"].map((key) => selected.profile[key] ? (
                                <div key={key} className="grid grid-cols-[170px_1fr] gap-4 border-b border-ink/10 py-3">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/45">{key.replace(/_/g, " ")}</span>
                                    <span className="break-words text-sm text-ink/75">{selected.profile[key]}</span>
                                </div>
                            ) : null)}
                        </div>
                        {selected.profile.description && <p className="mt-6 whitespace-pre-wrap text-sm leading-relaxed text-ink/70">{selected.profile.description}</p>}
                        <div className="mt-7 flex flex-wrap gap-2">
                            {(selected.documents || []).map((doc) => (
                                <button key={doc.id} type="button" data-testid={`admin-employer-doc-${doc.id}`} onClick={() => download(doc)} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink/60 hover:border-forest hover:text-forest"><Download size={14} /> {doc.original}</button>
                            ))}
                        </div>
                        <div className="mt-8">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink/45">Review notes</p>
                            <textarea data-testid="admin-verification-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inputClass} mt-3 resize-y`} placeholder="Optional note shown to the employer…" />
                            <div className="mt-4 flex flex-wrap gap-2">
                                {STATUSES.map((status) => (
                                    <button key={status} type="button" data-testid={`admin-verify-${status}`} onClick={() => setVerification(status)} className="border border-forest/25 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-forest hover:text-ivory">
                                        {status.replace(/_/g, " ")}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}
